import * as React from 'react';
import {FunctionComponent, useMemo, useRef} from 'react';
import {VictoryArea, VictoryAxis, VictoryChart, VictoryLine, VictoryVoronoiContainer} from 'victory';
import {SvgText} from '../Ui/Components/Utils/SvgText';

interface OwnProps {
    color?: string;
    height: number;
    padding?: {
        top?: number;
        bottom?: number;
        left?: number;
        right?: number;
    };
    axis?: {
        x?: Array<{
            value: number;
            label: string;
        }>;
        y?: Array<{
            value: number;
            label: string;
        }>;
    };
    values: Array<{x: number; y: number; label?: string}>;
    tooltip?: {
        x: number;
        content: any;
    } | null;
    onDataActivated?: (data: {point: {x: number; y: number; label?: string}; x: number}) => void;
}

const styles = {
    axis: {
        axis: {
            stroke: 'rgba(0, 0, 0, 0.1)',
        },
        grid: {
            stroke: 'rgba(0, 0, 0, 0.1)',
            strokeWidth: 1,
            strokeDasharray: 4,
        },
        tickLabels: {
            fill: '#000',
            fontSize: 8,
            padding: 0,
        },
    },
};

function reduceDataPoints(map: Array<{x: number; y: number}>): Array<{x: number; y: number}> {
    const epsilon = 0.01;

    //Recursive Ramer–Douglas–Peucker algorithm
    function perpendicularDistance(
        point: {x: number; y: number},
        lineStart: {x: number; y: number},
        lineEnd: {
            x: number;
            y: number;
        },
    ) {
        let dx = lineEnd.x - lineStart.x;
        let dy = lineEnd.y - lineStart.y;

        //Normalise
        const mag = Math.sqrt(dx * dx + dy * dy);
        if (mag > 0.0) {
            dx /= mag;
            dy /= mag;
        }

        const pvx = point.x - lineStart.x;
        const pvy = point.y - lineStart.y;

        //Get dot product (project pv onto normalized direction)
        const pvdot = dx * pvx + dy * pvy;

        //Scale line direction vector and subtract it from pv
        const ax = pvx - pvdot * dx;
        const ay = pvy - pvdot * dy;

        return Math.sqrt(ax * ax + ay * ay);
    }

    //Find the point with the maximum distance
    let dmax = 0;
    let index = 0;
    const end = map.length - 1;
    for (let i = 1; i < end; ++i) {
        const d = perpendicularDistance(map[i], map[0], map[end]);
        if (d > dmax) {
            index = i;
            dmax = d;
        }
    }

    //If max distance is greater than epsilon, recursively simplify
    if (dmax > epsilon) {
        //Recursive call
        const recResults1 = reduceDataPoints(map.slice(0, index + 1));
        const recResults2 = reduceDataPoints(map.slice(index));

        //Build the result list
        return recResults1.slice(0, recResults1.length - 1).concat(recResults2);
    } else {
        return [map[0], map[end]];
    }
}

export const PlainGraph: FunctionComponent<OwnProps> = ({
    tooltip,
    color,
    height,
    padding,
    axis,
    values,
    onDataActivated,
}) => {
    const ref = useRef<HTMLElement>(null);

    const defaultColor = '#3AC2E7';

    const uuid = useMemo(() => {
        //Random number to identify this graph
        return String(Math.floor(Math.random() * 1000000000));
    }, []);

    const lines = tooltip?.content.split('\n') || '';

    const dataPoints = useMemo(() => {
        const map = values.map((value) => {
            return {
                x: value.x,
                y: value.y,
            };
        });
        if (map.length < 800) {
            return map;
        }

        return reduceDataPoints(map);
    }, [values]);

    return (
        <>
            <VictoryChart
                height={height}
                padding={padding}
                animate={false}
                domain={{
                    x: [Math.min(...values.map((value) => value.x)), Math.max(...values.map((value) => value.x))],
                }}
                containerComponent={
                    <VictoryVoronoiContainer
                        // @ts-ignore
                        ref={ref}
                        voronoiDimension="x"
                        voronoiBlacklist={['area']}
                        onActivated={(data, props: any) => {
                            if (data.length > 0 && props.scale && props.scale.x) {
                                const point = values.find((value) => value.x === data[0].x);

                                if (point) {
                                    onDataActivated?.({
                                        point: point,
                                        x: props.scale.x(data[0].x),
                                    });
                                }
                            }
                        }}
                    />
                }
            >
                <>
                    {/* Gradient for the area, wrapped in a fragment to prevent invalid props to leak to the elements */}
                    <linearGradient id={uuid} x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor={color ? color : defaultColor} stopOpacity="0.4" />
                        <stop offset="100%" stopColor={color ? color : defaultColor} stopOpacity="0" />
                    </linearGradient>
                </>
                {axis?.x ? (
                    <VictoryAxis
                        fixLabelOverlap
                        style={styles.axis}
                        tickValues={axis.x.map((tick) => tick.value)}
                        tickFormat={(value) => {
                            return axis?.x?.find((tick) => tick.value === value)?.label ?? '';
                        }}
                    />
                ) : (
                    <VictoryAxis
                        style={{
                            axis: {stroke: 'transparent'},
                            ticks: {stroke: 'transparent'},
                            tickLabels: {fill: 'transparent'},
                        }}
                    />
                )}
                {axis?.y ? (
                    <VictoryAxis
                        style={styles.axis}
                        dependentAxis
                        fixLabelOverlap
                        orientation="left"
                        tickValues={axis.y.map((tick) => tick.value)}
                        tickFormat={(value: any) => {
                            return axis?.y?.find((tick) => tick.value === value)?.label ?? '';
                        }}
                    />
                ) : (
                    <VictoryAxis
                        style={{
                            axis: {stroke: 'transparent'},
                            ticks: {stroke: 'transparent'},
                            tickLabels: {fill: 'transparent'},
                        }}
                    />
                )}
                <VictoryArea
                    name="area"
                    data={dataPoints}
                    style={{
                        data: {
                            fill: `url(#${uuid})`,
                        },
                    }}
                />
                <VictoryLine
                    name="line"
                    data={dataPoints}
                    style={{
                        data: {
                            stroke: color ? color : defaultColor,
                            strokeWidth: 1,
                        },
                    }}
                />
                {tooltip && tooltip.content && (
                    <>
                        <line x1={tooltip.x} y1={0} x2={tooltip.x} y2={height - 20} stroke="black" strokeWidth="1" />
                        <g
                            transform={`translate(${
                                tooltip.x + 4 + 70 > (ref?.current as any)?.portalRef?.props.width
                                    ? tooltip.x - 74
                                    : tooltip.x + 4
                            }, 0)`}
                        >
                            <rect
                                key="background"
                                y="10"
                                width="70"
                                height="40"
                                rx="4"
                                fill="#FFFFFF"
                                stroke="#CCCCCC"
                                strokeWidth="1"
                            />
                            <SvgText key="line-1" x="8" y="22" fontSize={8} fontWeight={400} content={lines[0] || ''} />
                            <SvgText key="line-2" x="8" y="32" fontSize={8} fontWeight={400} content={lines[1] || ''} />
                            <SvgText key="line-3" x="8" y="42" fontSize={8} fontWeight={400} content={lines[2] || ''} />
                        </g>
                    </>
                )}
            </VictoryChart>
        </>
    );
};
